import React, { useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";

import { ModalBase } from "@performyard/py-components";

import Button from "../../../../components/Button";
import DeletableChip from "../../../../components/Chip/templates/DeletableChip";
import Checkbox from "../../../../components/Inputs/Checkbox";
import InputText from "../../../../components/Inputs/InputText";
import { Checkboxes } from "../../../../components/Inputs/SelectionOptions/Checkboxes";
import { RadioButtons } from "../../../../components/Inputs/SelectionOptions/RadioButtons";
import Spacer from "../../../../components/Spacer";
import Spinner from "../../../../components/Spinner";
import Typography from "../../../../components/Text";

import { useAiSettings } from "../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";
import useToast from "../../../../hooks/useToast";

import { uploadFile } from "../../../../api/services/defs/documents";

import { getVisibilityOptions, useIsCompanyDocumentPage } from "../../helpers";
import { DocumentFile, DocumentVisibility } from "../../schema";
import { UploadDocumentModalProps } from "../../schema";

const UploadDocumentModal = ({ show, onClose, userData, documents, setDocuments }: UploadDocumentModalProps) => {
	const toast = useToast();
	const isCompanyDocumentPage = useIsCompanyDocumentPage();

	// Check if AI features should be shown (requires both AI FF and Conversational AI FF)
	const { value: aiFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);
	const { value: conversationalAIFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-conversational-ui-enabled", false);
	const { data: aiSettings } = useAiSettings();
	const isAiEnabled =
		aiFeatureFlagEnabled && conversationalAIFeatureFlagEnabled && aiSettings?.conversationalAiEnabled;

	// Memoize visibility options to prevent unnecessary recalculations
	const visibilityOptions = useMemo(() => {
		if (!userData?.employeeName) {
			return [];
		}
		return getVisibilityOptions(isCompanyDocumentPage, userData.employeeName);
	}, [isCompanyDocumentPage, userData?.employeeName]);

	const fileInputRef = useRef<HTMLInputElement>(null);
	// Initialize with "Everyone" selected for company documents
	const [docVisibilities, setDocVisibilities] = useState<DocumentVisibility[]>(() => {
		if (isCompanyDocumentPage && visibilityOptions.length > 0) {
			return [visibilityOptions[0]]; // Default to first option (Everyone)
		}
		return visibilityOptions || [];
	});
	const [isUploading, setIsUploading] = useState<boolean>(false);
	const [tagValue, setTagValue] = useState("");
	const [tags, setTags] = useState<string[]>([]);
	const [allowAiRetrieval, setAllowAiRetrieval] = useState<boolean>(true);

	const onDrop = async (acceptedFiles) => {
		const uploadUrl = isCompanyDocumentPage ? "/api/upload-company-document/" : "/api/upload_document/";
		const uploadPromises: Promise<any>[] = userData?.employeeID
			? acceptedFiles.map((file) =>
					uploadFile(file, uploadUrl, docVisibilities, userData.employeeID, tags, allowAiRetrieval)
				)
			: []; // added a default of an uploadPromises=empty array if userData.employeeID does not exist here

		const successResults: DocumentFile[] = [];
		const errors: string[] = [];

		setIsUploading(true);

		for (const promise of uploadPromises) {
			try {
				const result = await promise;
				successResults.push(result);
			} catch (err: any) {
				errors.push(err);
				continue;
			}
		}

		setIsUploading(false);
		handleCloseModal();

		if (successResults.length > 0) {
			const updatingDocuments = { ...documents };
			// depending on the page location, we need to update the documents state
			if (isCompanyDocumentPage) {
				updatingDocuments.companyFiles = updatingDocuments.companyFiles.concat(successResults);
			} else {
				updatingDocuments.myFiles = updatingDocuments.myFiles.concat(successResults);
			}
			setDocuments(updatingDocuments);
			toast("Successfully uploaded", {
				type: "success",
				duration: 5000,
				size: "small",
			});
		}

		if (errors.length > 0) {
			toast("One or more uploads failed: " + errors[0], {
				type: "error",
				duration: 5000,
				size: "small",
			});
		}
	};

	const handleChooseFile = () => {
		fileInputRef.current?.click();
	};

	const handleAddTag = () => {
		if (tagValue.trim() === "") {
			setTagValue("");
			return;
		}
		setTags((prev) => [...prev.filter((t) => t !== tagValue), tagValue]);
		setTagValue("");
	};

	const handleCloseModal = () => {
		// Clear existing states
		setTags([]);
		setTagValue("");
		setAllowAiRetrieval(true);
		// Reset to default visibility (Everyone for company docs)
		if (isCompanyDocumentPage && visibilityOptions.length > 0) {
			setDocVisibilities([visibilityOptions[0]]);
		} else {
			setDocVisibilities(visibilityOptions);
		}
		onClose();
	};

	const { getRootProps, getInputProps } = useDropzone({ onDrop });

	const content = (
		<div
			{...getRootProps()}
			onClick={(e) => {
				e.stopPropagation();
			}}
		>
			<input {...getInputProps()} ref={fileInputRef} />
			<div style={{ textAlign: "center" }}>
				<h3 style={{ fontSize: "20px", fontWeight: 600, margin: 0 }}>Upload Documents</h3>
			</div>
			<Spacer height={2} />
			{isCompanyDocumentPage && (
				<>
					<div style={{ textAlign: "left" }}>
						<Typography size="paragraph-3" weight="semi-bold">
							Tags
						</Typography>
						<Spacer height={1} />
						<div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
							<div style={{ flex: 1 }}>
								<InputText
									onChange={(value) => setTagValue(value)}
									value={tagValue}
									placeholder="Press 'enter' or click '+Add' button"
									onEnter={handleAddTag}
								/>
							</div>
							<Button color="blue" size="medium" onClick={handleAddTag}>
								+ Add
							</Button>
						</div>
						<Spacer height={1} />
						{tags.length > 0 &&
							tags.map((tag) => (
								<DeletableChip
									key={tag}
									label={tag}
									onDelete={() => setTags(tags.filter((t) => t !== tag))}
								/>
							))}
					</div>
					<Spacer height={2} />
				</>
			)}
			<div style={{ textAlign: "left" }}>
				<Typography size="paragraph-3" weight="semi-bold">
					Who can see this file?
				</Typography>
				<Spacer height={1} />
				{isCompanyDocumentPage ? (
					<RadioButtons
						value={docVisibilities[0] || null}
						options={visibilityOptions}
						onChange={(val) => {
							setDocVisibilities([val]);
						}}
					/>
				) : (
					<Checkboxes
						value={docVisibilities || []}
						options={visibilityOptions || []}
						onChange={(val) => setDocVisibilities(val)}
					/>
				)}
			</div>
			{isCompanyDocumentPage && isAiEnabled && (
				<>
					<Spacer height={2} />
					<div style={{ display: "flex", alignItems: "flex-start", gap: "8px", textAlign: "left" }}>
						<Checkbox value={allowAiRetrieval} onChange={setAllowAiRetrieval} />
						<Typography size="paragraph-3" weight="regular">
							Allow Ask PerformYard feature to retrieve and reference this document when answering
							questions
						</Typography>
					</div>
				</>
			)}
			<Spacer height={3} />
			<div style={{ display: "flex", justifyContent: "center" }}>
				<Button color="green" size="large" onClick={handleChooseFile}>
					Choose Files
				</Button>
			</div>
			<Spacer height={3} />
			<div style={{ textAlign: "center" }}>
				<Typography size="paragraph-2" weight="regular" color="neutral-5">
					Or drag files into this modal
				</Typography>
			</div>
		</div>
	);

	const loadingContent = (
		<div className="flex items-center justify-center">
			<Spinner borderWidth={4} size={80} />
		</div>
	);

	return (
		<ModalBase isOpen={show} onClose={handleCloseModal}>
			<div className="w-[416px] px-6 pb-6">{isUploading ? loadingContent : content}</div>
		</ModalBase>
	);
};

export default UploadDocumentModal;
